import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import SeoHelmet from '../../common/SeoHelmet';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const MeyerhofTheory = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Bearing Capacity of Shallow Foundations - Meyerhof (1963) | Theory"
        description="Theory and formulation of Meyerhof’s (1963) bearing capacity method for shallow foundations, incorporating shape, depth, and load inclination factors for practical design applications."
        url="https://www.geocalc.app/meyerhof-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Meyerhof Bearing Capacity | Theory', url: 'https://www.geocalc.app/meyerhof-theory' },
        ]}
      />

      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>Bearing Capacity of Shallow Foundations - Meyerhof (1963) | Theory & Formulations</h1>
          <h2>(Based on "Principles of Foundation Engineering", 4th Edition by Braja M. Das)</h2>

          <p>Meyerhof (1963) expanded on Terzaghi&rsquo;s bearing capacity theory by:</p>
          <ul>
            <li>
              Introducing <strong>depth factors</strong>
            </li>
            <li>
              Modifying the <strong>bearing capacity factors</strong> (N<sub>c</sub>, N<sub>q</sub>, N<sub>&gamma;</sub>)
            </li>
          </ul>
          <p>
            He recognized that foundation depth, footing shape, and load inclination significantly affect the ultimate bearing
            capacity.
          </p>

          <h3>Ultimate Bearing Capacity Formula:</h3>
          <p>
            q<sub>u</sub> = c' N<sub>c</sub> s<sub>c</sub> d<sub>c</sub> i<sub>c</sub> + q N<sub>q</sub> s<sub>q</sub> d
            <sub>q</sub> i<sub>q</sub> + (1/2) &gamma; B N<sub>&gamma;</sub> s<sub>&gamma;</sub> d<sub>&gamma;</sub> i
            <sub>&gamma;</sub>
          </p>

          <h3>Where:</h3>
          <ul>
            <li>c' = effective cohesion</li>
            <li>
              q = overburden pressure at the foundation base (q = &gamma; D<sub>f</sub>)
            </li>
            <li>&gamma; = unit weight of soil</li>
            <li>B = width of foundation</li>
            <li>
              N<sub>c</sub>, N<sub>q</sub>, N<sub>&gamma;</sub> = bearing capacity factors depending on soil friction angle &phi;'
            </li>
            <li>s = shape factors</li>
            <li>d = depth factors</li>
            <li>i = load inclination factors</li>
          </ul>

          <h3>Key Additions by Meyerhof:</h3>
          <ul>
            <li>
              <strong>Depth Factors</strong> (d<sub>c</sub>, d<sub>q</sub>, d<sub>&gamma;</sub>)<br />
              - Foundation embedment depth increases bearing capacity.
              <br />
            </li>
            <li>
              <strong>Shape Factors</strong> (s<sub>c</sub>, s<sub>q</sub>, s<sub>&gamma;</sub>)<br />
              - Different footing shapes have different capacities.
              <br />
            </li>
            <li>
              <strong>Load Inclination Factors</strong> (i<sub>c</sub>, i<sub>q</sub>, i<sub>&gamma;</sub>)<br />
              - Inclined loads reduce bearing capacity.
              <br />
            </li>
            <li>
              <strong>Modified Bearing Capacity Factors</strong>
              <br />- Updated formulas for N<sub>c</sub>, N<sub>q</sub>, N<sub>&gamma;</sub> compared to Terzaghi.
              <br />
            </li>
          </ul>

          <h3>Practical Simplifications:</h3>
          <ul>
            <li>
              For <strong>vertical loads</strong> on <strong>level ground</strong> with <strong>horizontal bases</strong>,
              inclination factors (i) are taken as 1.
            </li>
            <li>
              For <strong>strip footings</strong>, shape factors (s) are usually close to 1.
            </li>
          </ul>

          <h2>Summary:</h2>
          <p>Meyerhof&rsquo;s method improved the classical bearing capacity theory by:</p>
          <ul>
            <li>
              Including the <strong>depth effect</strong>
            </li>
            <li>
              Modifying <strong>bearing capacity factors</strong> for better accuracy
            </li>
            <li>
              Introducing <strong>shape</strong> and <strong>load inclination corrections</strong>
            </li>
            <li>
              Making the theory <strong>more general</strong> and <strong>closer to field conditions</strong> than
              Terzaghi&rsquo;s formula
            </li>
          </ul>
        </div>
        <div className="theoryButtonContainer">
          <Link to={'/calculations/meyerhof'}>
            <Button variant="outlined" size="small">
              GO TO MEYERHOF CALCULATOR
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MeyerhofTheory;
